/* Atlas v3 — "The Weekly": the Insights editorial surface (phone).

   Rewritten 2026-08-24 to match the DESKTOP Insights layout
   (src/v3desktop/pages/Insights.jsx): a lead story then the week's
   write-ups grouped by bucket, each tapping open to the full-article
   reader. The old phone-only "magazine edition" (cover masthead +
   Sector Shifts + Signal Watch + Week Ahead + Scorecard, all built by a
   buildEdition() adapter) was dropped — it wrapped the same stories in
   scaffolding that read as half-empty/sloppy and buried the actual
   write-ups. One page, one shape, matching desktop.

   Brings its own shell (TickerStrip + BottomNav) like Overview / MyStocks —
   NOT wrapped in the old AppLayout.

   Ticker chips → openTicker(symbol) via the shared StockDetail drawer.
   A story tap → /insights/:id (the existing InsightReader).

   DATA: useInsights().buckets (inflection / capital_flow / earnings_tells /
   quiet_signals), written weekly by the generate-insights cron. Empty
   sources just hide their section — no seeded/sample data. */
import { useMemo } from 'react'
import { TickerCtx, useTicker } from './lib'
import { TickerStrip, BottomNav, SearchPill } from './shell'
import { StockLogo } from '@/v2/components/StockLogo'
import { StockDetailProvider, useStockDetail } from '@/v2/lib/StockDetailProvider'
import { v2Navigate } from '@/v2/lib/useV2Path'
import { useInsights } from '@/v2/hooks/useInsights'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Serif = { fontFamily: 'var(--studio-font-display)', fontStyle: 'normal', fontWeight: 400 }
const Sans = { fontFamily: 'var(--studio-font-sans)' }
const HAIR = '1px solid hsl(var(--studio-border) / 0.7)'

const BUCKETS = [
  ['inflection', 'Inflection'],
  ['capital_flow', 'Capital Flow'],
  ['earnings_tells', 'Earnings Tells'],
  ['quiet_signals', 'Quiet Signals'],
]

function tickersOf(story) {
  return (story.tickers || [])
    .map((t) => (typeof t === 'string' ? { ticker: t } : t))
    .filter((t) => t.ticker)
}

/* ── section eyebrow: amber dot + mono-caps label + 2-digit index ── */
function Kicker({ label, n }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'hsl(var(--studio-accent))', flexShrink: 0 }} />
      <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.2em', color: 'hsl(var(--studio-accent))' }}>{label}</span>
      <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))' }}>&nbsp;·&nbsp; {n}</span>
    </div>
  )
}

/* ── ticker chips — logo + symbol, tap opens the drawer (lead chip · marked) ── */
function TickerChips({ story }) {
  const openTicker = useTicker()
  const ts = tickersOf(story)
  if (ts.length === 0) return null
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
      {ts.map((t) => (
        <button key={t.ticker} onClick={(e) => { e.stopPropagation(); openTicker(t.ticker) }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 999, border: '1px solid hsl(var(--studio-border))', background: 'transparent', cursor: 'pointer' }}>
          <StockLogo ticker={t.ticker} size={15} rounded="50%" />
          <span style={{ ...Mono, fontSize: 11, color: 'hsl(var(--studio-text))' }}>{t.ticker}{t.is_lead ? ' ·' : ''}</span>
        </button>
      ))}
    </div>
  )
}

/* ── loading + empty states ── */
function WeeklyNote({ children }) {
  return (
    <div className="atlas-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 32px', textAlign: 'center' }}>
      <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.22em', color: 'hsl(var(--studio-accent))', marginBottom: 14 }}>THE WEEKLY</div>
      <div style={{ ...Sans, fontSize: 13, lineHeight: 1.6, color: 'hsl(var(--studio-text-subtle))', maxWidth: 260 }}>{children}</div>
    </div>
  )
}

function InsightsBody() {
  const { openTicker } = useStockDetail()
  const { buckets, loading } = useInsights()

  const all = useMemo(() => BUCKETS.flatMap(([k]) => buckets[k] || []), [buckets])
  const hasAny = all.length > 0
  const lead = useMemo(
    () => all.find((s) => s.is_hero) || (buckets.inflection || [])[0] || all[0] || null,
    [all, buckets],
  )
  const openStory = (id) => { if (id) v2Navigate(`/insights/${id}`) }

  return (
    <TickerCtx.Provider value={openTicker}>
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
        background: 'radial-gradient(140% 75% at 50% -10%, hsl(var(--studio-background)) 0%, hsl(var(--studio-overlay)) 100%)' }}>

        {/* page header — kicker + serif title + search pill (matches every v3 screen) */}
        <div style={{ flexShrink: 0, padding: '14px 24px 10px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.22em', color: 'hsl(var(--studio-accent))', marginBottom: 4 }}>THE WEEKLY</div>
            <div style={{ ...Serif, fontStyle: 'italic', fontSize: 28, lineHeight: 1, color: 'hsl(var(--studio-text))' }}>Insights</div>
          </div>
          <SearchPill />
        </div>

        {!hasAny
          ? (loading
            ? <WeeklyNote>Loading this week&rsquo;s edition&hellip;</WeeklyNote>
            : <WeeklyNote>No briefing yet — The Weekly is written every Friday night.</WeeklyNote>)
          : (
            <div className="atlas-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>

              {/* ── THE LEAD · 01 ── */}
              {lead && (
                <div style={{ padding: '8px 24px 24px', borderBottom: HAIR }}>
                  <Kicker label="THE LEAD" n={`01${lead.sector_primary ? ` · ${lead.sector_primary}` : ''}`} />
                  <button onClick={() => openStory(lead.id)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: lead.id ? 'pointer' : 'default' }}>
                    <div style={{ ...Serif, fontSize: 28, lineHeight: 1.12, letterSpacing: '-0.01em', color: 'hsl(var(--studio-text))', textWrap: 'pretty' }}>{lead.headline}</div>
                    {lead.snippet && (
                      <p style={{ ...Sans, fontSize: 13.5, lineHeight: 1.58, color: 'hsl(var(--studio-text-muted))', margin: '12px 0 0', textWrap: 'pretty' }}>{lead.snippet}</p>
                    )}
                    {lead.id && (
                      <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.1em', color: 'hsl(var(--studio-accent))', marginTop: 13 }}>READ THE STORY →</div>
                    )}
                  </button>
                  <TickerChips story={lead} />
                </div>
              )}

              {/* ── bucket sections — each write-up taps open to the reader ── */}
              {BUCKETS.map(([key, label], bi) => {
                const stories = (buckets[key] || []).filter((s) => s !== lead)
                if (stories.length === 0) return null
                return (
                  <div key={key} style={{ padding: '22px 24px 4px', borderTop: bi === 0 ? 'none' : HAIR }}>
                    <Kicker label={label.toUpperCase()} n={`0${bi + 2}`} />
                    {stories.map((s, i) => (
                      <div key={s.id || i}>
                        <button onClick={() => openStory(s.id)}
                          style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '2px 0 0', cursor: s.id ? 'pointer' : 'default' }}>
                          <div style={{ ...Serif, fontSize: 20, lineHeight: 1.22, color: 'hsl(var(--studio-text))', textWrap: 'pretty' }}>{s.headline}</div>
                          {s.snippet && (
                            <p style={{ ...Sans, fontSize: 12.5, lineHeight: 1.5, color: 'hsl(var(--studio-text-muted))', margin: '8px 0 0', textWrap: 'pretty' }}>{s.snippet}</p>
                          )}
                        </button>
                        <TickerChips story={s} />
                        {i < stories.length - 1 && <div style={{ height: 1, background: 'hsl(var(--studio-border) / 0.5)', margin: '18px 0' }} />}
                      </div>
                    ))}
                  </div>
                )
              })}

              <div style={{ height: 30 }} />
            </div>
          )}

        <div style={{ flexShrink: 0, borderTop: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-background) / 0.72)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
          <TickerStrip />
        </div>
        <BottomNav />
      </div>
    </TickerCtx.Provider>
  )
}

export function Insights() {
  return (
    <div className="v3-root" style={{ height: 'var(--v3-vh, 100dvh)', display: 'flex', flexDirection: 'column' }}>
      <StockDetailProvider>
        <InsightsBody />
      </StockDetailProvider>
    </div>
  )
}
